/**
 * PWA glue — service worker registration, update signalling, install prompt.
 *
 * The worker itself is src/sw.js (copied to the deploy root, see
 * scripts/build.js). Nothing here runs in dev or inside the native wrappers:
 *
 *   dev       the zero-build loop serves raw modules from src/; a worker
 *             caching them would serve yesterday's code after every edit
 *   native    iOS and Electron load from a custom `game://` scheme, where
 *             registration throws outright; Android's WebViewAssetLoader is a
 *             real https origin, but its assets are already on the device, so
 *             a cache in front of them is pure overhead
 *
 * Events dispatched on `window`, so the UI can stay out of this file:
 *
 *   lots:installable   the browser will accept an install prompt
 *   lots:installed     the app was installed
 *   lots:update-ready  a new deploy is cached and waiting for a reload
 */

import { getSurface, isDevMode } from './platform.js';

/** @type {Event & {prompt: () => void, userChoice: Promise<{outcome: string}>} | null} */
let installPrompt = null;

/** @type {ServiceWorkerRegistration | null} */
let waitingRegistration = null;

/**
 * Register the service worker and start listening for install/update events.
 * Safe to call anywhere — it no-ops in every environment that shouldn't have a
 * worker, and never rejects.
 * @returns {void}
 */
export function initPwa() {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return;

    // Capturing this is independent of the worker: the browser fires it before
    // registration settles, and missing it means no install button at all.
    window.addEventListener('beforeinstallprompt', (event) => {
        event.preventDefault(); // suppress Chrome's own mini-infobar
        installPrompt = /** @type {any} */ (event);
        window.dispatchEvent(new CustomEvent('lots:installable'));
    });

    window.addEventListener('appinstalled', () => {
        installPrompt = null;
        // Now that the game lives on the home screen, ask the browser not to
        // evict its caches — up to ~15MB of soundtrack and models. Requesting
        // this on a casual first visit would put a permission prompt in front
        // of someone who has not asked for anything yet.
        requestPersistentStorage();
        window.dispatchEvent(new CustomEvent('lots:installed'));
    });

    // A service worker is ours to manage only where we are the whole page: a
    // native build ships its assets already, and a portal frames us inside a
    // site whose caching is not ours to fight.
    const surface = getSurface();
    if (surface !== 'web' && surface !== 'pwa') return;
    if (isDevMode()) return;
    if (!('serviceWorker' in navigator)) return;

    // Relative to the document, not this module: the worker sits at the deploy
    // root so its scope covers the whole origin, while this file is in js/.
    // updateViaCache 'none' keeps the browser's HTTP cache out of sw.js update
    // checks entirely, whatever headers a proxy or CDN put on it.
    navigator.serviceWorker.register('sw.js', { updateViaCache: 'none' }).then((registration) => {
        if (registration.waiting) announceUpdate(registration);

        // Ask for update checks instead of waiting for the browser's own:
        // those are throttled (Chrome guarantees one only every ~24h), which
        // left returning players pinned to the previous deploy long after a
        // release. Check on load, whenever the tab comes back into view — the
        // iOS home-screen app resuming, a desktop tab refocused after days —
        // and hourly while it stays open. A check is one conditional GET of
        // sw.js; a failed one (offline) costs nothing.
        const checkForUpdate = () => { registration.update().catch(() => { /* offline or mid-install; the next check will land */ }); };
        // Not immediately: an update() issued while the register job is still
        // settling is coalesced into it and never reaches the network
        // (observed in Chromium — the delayed call fetches, the immediate one
        // does not). Chromium may then defer the actual fetch up to ~1 minute
        // after navigation, so a quiet network right after this fires is
        // expected — the check lands within the first minute of play.
        setTimeout(checkForUpdate, 4000);
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') checkForUpdate();
        });
        setInterval(checkForUpdate, 60 * 60 * 1000);

        registration.addEventListener('updatefound', () => {
            const installing = registration.installing;
            if (!installing) return;
            installing.addEventListener('statechange', () => {
                // A worker reaching `installed` with a controller already in
                // place is an update; without one it is the very first install,
                // which needs no announcement.
                if (installing.state === 'installed' && navigator.serviceWorker.controller) {
                    announceUpdate(registration);
                }
            });
        });
    }).catch((err) => {
        // A failed registration costs offline support, not the game.
        console.warn('[pwa] service worker registration failed:', err);
    });

    if (surface === 'pwa') requestPersistentStorage();
}

/**
 * @param {ServiceWorkerRegistration} registration Registration holding the update.
 * @returns {void}
 */
function announceUpdate(registration) {
    waitingRegistration = registration;
    window.dispatchEvent(new CustomEvent('lots:update-ready'));
}

/** @returns {boolean} Whether promptInstall() has a prompt to show. */
export function isInstallAvailable() {
    return installPrompt !== null;
}

/**
 * Show the browser's install prompt. Must be called from a user gesture.
 * @returns {Promise<'accepted'|'dismissed'|'unavailable'>} What the player chose.
 */
export async function promptInstall() {
    if (!installPrompt) return 'unavailable';
    const prompt = installPrompt;
    // One prompt event is good for one call, whatever the outcome.
    installPrompt = null;
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    return outcome === 'accepted' ? 'accepted' : 'dismissed';
}

/** @returns {boolean} Whether a new deploy is cached and waiting. */
export function isUpdateReady() {
    return waitingRegistration !== null;
}

/**
 * Activate a waiting update and reload onto it. Caller decides when — doing it
 * automatically would reload the tab in the middle of a match.
 * @returns {void}
 */
export function applyUpdate() {
    const waiting = waitingRegistration?.waiting;
    if (!waiting) return;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
        window.location.reload();
    }, { once: true });
    waiting.postMessage('SKIP_WAITING');
}

/**
 * Ask for storage that survives eviction pressure. Chrome decides silently on
 * engagement; Firefox prompts, which is why this is not called on page load.
 * @returns {void}
 */
function requestPersistentStorage() {
    navigator.storage?.persisted?.()
        .then((already) => (already ? true : navigator.storage.persist()))
        .catch(() => { /* best effort — the caches still work, just evictable */ });
}
